import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";

const BG_URL =
  "https://images.unsplash.com/photo-1496865534669-25ec2a3a0fd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnklMjBtaW5pbWFsfGVufDB8fHx8MTc4MjY5MTY0OXww&ixlib=rb-4.1.0&q=85";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    brokerage: "",
    license_no: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account created");
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-neutral-200" style={{
        backgroundImage: `linear-gradient(rgba(10,10,10,0.40), rgba(10,10,10,0.60)), url(${BG_URL})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        <div className="text-white" data-testid="register-core-logo-top">
          <CoreLogo variant="full" size={48} className="text-white" />
        </div>
        <div className="text-white max-w-md">
          <div className="font-mono text-xs uppercase tracking-[0.3em] opacity-80 mb-8">Ledger / 01</div>
          <h1 className="font-serif text-5xl leading-tight">Year-end taxes, without the spreadsheet panic.</h1>
          <p className="mt-6 font-sans text-sm opacity-80">Log deals as you close them. Track deductible expenses as you spend. Export an IRS-ready report in one click.</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 bg-white">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8 text-neutral-900" data-testid="register-core-logo-mobile">
            <CoreLogo variant="inline" size={42} />
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Open an Agent Account</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-neutral-900 mb-2">Start your ledger.</h2>
          <p className="text-sm text-neutral-500 mb-10">For licensed real estate agents and brokers.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Full name</Label>
              <Input required value={form.full_name} onChange={update("full_name")}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="register-fullname-input" />
            </div>
            <div>
              <Label className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Email</Label>
              <Input required type="email" value={form.email} onChange={update("email")}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Password</Label>
              <Input required type="password" minLength={6} value={form.password} onChange={update("password")}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="register-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Brokerage</Label>
                <Input value={form.brokerage} onChange={update("brokerage")}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="register-brokerage-input" />
              </div>
              <div>
                <Label className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">License #</Label>
                <Input value={form.license_no} onChange={update("license_no")}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="register-license-input" />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-11 font-sans tracking-wide mt-2"
              data-testid="register-submit-button"
            >
              {submitting ? "Creating account…" : "Create account"}
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-neutral-200 text-sm text-neutral-500">
            Already have an account?{" "}
            <Link to="/login" className="text-neutral-900 underline underline-offset-4" data-testid="register-go-login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
